import advancedToolsImage from '@/shared/assets/images/advanced-tools-tooltip.png';
import allLanguagesImage from '@/shared/assets/images/all-languages-tooltip.png';
import demoProjectWelcomeImage from '@/shared/assets/images/demo-project-welcome.png';
import downloadVideoAnimationData from '@/shared/assets/lottie/download-video.json';
import audioTrackEditorAnimationData from '@/shared/assets/lottie/project-audio-track-editor.json';
import changeTextSectionAnimationData from '@/shared/assets/lottie/project-text-section.json';
import voiceCloneAnimationData from '@/shared/assets/lottie/project-voice-clone.json';
import { DOWNLOAD_OPTIONS_LINK, HOW_TO_EDIT_PROJECT_LINK, TEAMSPACES_DOC_LINK } from '@/shared/constants/docLinks';
import { Translate } from '@/shared/i18n';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { OnboardingStepKeys } from '@/shared/types/Onboarding';

import { OnboardingStep } from './types';

export const getOnboardingSteps = (t: Translate): Record<OnboardingStepKeys, OnboardingStep> => {
  return {
    advancedTools_v1: {
      title: t('editor.onboarding.advancedToolsTitle'),
      hasHighlight: false,
      imagePlacement: 'top',
      imageSrc: advancedToolsImage as string,
      minHeight: 118,
      placement: 'bottom',
      subtitle: t('editor.onboarding.advancedToolsSubtitle'),
      type: 'tooltip',
      width: 212,
    },
    audioTrackEditor_v1: {
      title: t('editor.onboarding.audioTrackTitle'),
      actions: [
        {
          id: 1,
          title: t('editor.action.gotIt'),
          type: 'button',
          handler: MixpanelEvents.onboardingStepAudioTrackEditorGotIt,
        },
      ],
      animationData: audioTrackEditorAnimationData,
      hasHighlight: true,
      type: 'tooltip',
      width: 280,
    },
    changeTextSection_v1: {
      title: t('editor.onboarding.changeTextTitle'),
      actions: [
        {
          id: 1,
          title: t('editor.action.learnMore'),
          type: 'link',
          handler: () => {
            MixpanelEvents.onboardingStepEditorLearnMore();
            window.open(HOW_TO_EDIT_PROJECT_LINK);
          },
        },
        {
          id: 2,
          title: t('editor.action.gotIt'),
          type: 'button',
          handler: MixpanelEvents.onboardingStepEditorGotIt,
        },
      ],
      animationData: changeTextSectionAnimationData,
      hasHighlight: true,
      placement: 'right',
      type: 'tooltip',
      width: 254,
    },
    clickToPlay_v1: {
      title: t('editor.onboarding.clickPlayTitle'),
      actions: [
        {
          id: 1,
          title: t('editor.action.okay'),
          type: 'button',
          handler: MixpanelEvents.onboardingStepPlayerOkay,
        },
      ],
      hasHighlight: true,
      placement: 'left',
      type: 'tooltip',
      width: 210,
    },
    customizeVoiceClone_v1: {
      title: t('editor.onboarding.customizeVoiceTitle'),
      actions: [
        {
          id: 1,
          title: t('editor.action.gotIt'),
          type: 'button',
          handler: MixpanelEvents.onboardingStepVoiceGotIt,
        },
      ],
      animationData: voiceCloneAnimationData,
      hasHighlight: true,
      placement: 'bottom',
      type: 'tooltip',
      width: 281,
    },
    demoProjectWelcome_v1: {
      title: t('editor.onboarding.demoWelcomeTitle'),
      actions: [
        {
          id: 1,
          title: t('editor.action.getStarted'),
          type: 'button',
        },
      ],
      hasHighlight: true,
      imageSrc: demoProjectWelcomeImage as string,
      subtitle: t('editor.onboarding.demoWelcomeSubtitle'),
      type: 'modal',
    },
    discoverTeamspace_v1: {
      title: t('editor.onboarding.discoverTeamspaceTitle'),
      actions: [
        {
          id: 1,
          title: t('editor.action.learnMore'),
          type: 'link',
          handler: () => {
            MixpanelEvents.discoverTeamspaceLearnMore();
            window.open(TEAMSPACES_DOC_LINK);
          },
        },
        {
          id: 2,
          title: t('editor.action.gotIt'),
          type: 'button',
        },
      ],
      hasHighlight: false,
      placement: 'right-end',
      subtitle: t('editor.onboarding.discoverTeamspaceSubtitle'),
      type: 'tooltip',
      width: 212,
    },
    downloadVideo_v1: {
      title: t('editor.onboarding.downloadTitle'),
      actions: [
        {
          id: 1,
          title: t('editor.action.learnMore'),
          type: 'link',
          handler: () => {
            MixpanelEvents.onboardingStepDownloadLearnMore();
            window.open(DOWNLOAD_OPTIONS_LINK);
          },
        },
        {
          id: 2,
          title: t('editor.action.gotIt'),
          type: 'button',
          handler: MixpanelEvents.onboardingStepDownloadGotIt,
        },
      ],
      animationData: downloadVideoAnimationData,
      hasHighlight: true,
      placement: 'top',
      type: 'tooltip',
      width: 202,
    },
    editorActions_v1: {
      title: t('editor.onboarding.editorActionsTitle'),
      actions: [{ id: 1, title: t('editor.action.gotIt'), type: 'button' }],
      hasHighlight: true,
      placement: 'right',
      subtitle: t('editor.onboarding.editorActionsSubtitle'),
      type: 'tooltip',
      width: 280,
      onRender: MixpanelEvents.editorAdoptionOnboardingShown,
    },
    manageTeamspace_v1: {
      title: t('editor.onboarding.manageTeamspaceTitle'),
      actions: [
        {
          id: 1,
          title: t('editor.action.gotIt'),
          type: 'button',
        },
      ],
      hasHighlight: false,
      placement: 'right',
      subtitle: t('editor.onboarding.manageTeamspaceSubtitle'),
      type: 'tooltip',
      width: 212,
    },
    segmentPreview_v1: {
      title: t('editor.onboarding.segmentPreviewTitle'),
      actions: [
        {
          id: 1,
          title: t('editor.action.gotIt'),
          type: 'button',
          handler: MixpanelEvents.onboardingStepSegmentPreviewGotIt,
        },
      ],
      hasHighlight: true,
      placement: 'bottom',
      subtitle: t('editor.onboarding.segmentPreviewSubtitle'),
      type: 'tooltip',
      width: 260,
    },
    speakerVoicesButton_v1: {
      title: t('editor.onboarding.speakerVoicesTitle'),
      actions: [
        {
          id: 1,
          title: t('editor.action.gotIt'),
          type: 'button',
          handler: MixpanelEvents.onboardingStepApplyChangesGotIt,
        },
      ],
      hasHighlight: true,
      placement: 'right',
      type: 'tooltip',
      width: 248,
    },
    transcriptVersions_v1: {
      title: t('editor.onboarding.transcriptVersionsTitle'),
      actions: [
        {
          id: 1,
          title: t('editor.action.gotIt'),
          type: 'button',
          handler: MixpanelEvents.onboardingStepTranscriptVersionsGotIt,
        },
      ],
      hasHighlight: true,
      placement: 'bottom',
      subtitle: t('editor.onboarding.transcriptVersionsSubtitle'),
      type: 'tooltip',
      width: 260,
    },
    translateMyOwnVideoButton_v1: {
      title: t('editor.onboarding.translateOwnTitle'),
      actions: [
        {
          id: 1,
          title: t('editor.action.gotIt'),
          type: 'button',
        },
      ],
      hasHighlight: true,
      imageSrc: allLanguagesImage as string,
      minHeight: 223,
      placement: 'bottom',
      type: 'tooltip',
      width: 281,
      onRender: MixpanelEvents.onboardingFinishedEditorDemo,
    },
    translationVariants_v1: {
      title: t('editor.onboarding.translationVariantsTitle'),
      actions: [
        {
          id: 1,
          title: t('editor.action.gotIt'),
          type: 'button',
          handler: MixpanelEvents.onboardingStepTranslationVariantsGotIt,
        },
      ],
      hasHighlight: true,
      placement: 'top',
      subtitle: t('editor.onboarding.translationVariantsSubtitle'),
      type: 'tooltip',
      width: 260,
    },
    videoPlayerTabs_v1: {
      title: t('editor.onboarding.videoTabsTitle'),
      actions: [
        {
          id: 1,
          title: t('editor.action.gotIt'),
          type: 'button',
        },
      ],
      hasHighlight: true,
      placement: 'left',
      type: 'tooltip',
      width: 246,
    },
  };
};

export const stepsWithNextStep = new Set<OnboardingStepKeys>([
  'advancedTools_v1',
  'audioTrackEditor_v1',
  'changeTextSection_v1',
  'clickToPlay_v1',
  'customizeVoiceClone_v1',
  'demoProjectWelcome_v1',
  'editorActions_v1',
  'segmentPreview_v1',
  'speakerVoicesButton_v1',
  'transcriptVersions_v1',
  'translateMyOwnVideoButton_v1',
  'translationVariants_v1',
  'videoPlayerTabs_v1',
]);
